import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export const RestockPage = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [p, r] = await Promise.all([
        api.get('/products'),
        api.get('/restock-requests')
      ]);
      setProducts(p.data);
      setRequests(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async () => {
    if (!productId) {
      toast.error('Select a product');
      return;
    }
    try {
      await api.post('/restock-requests', { product_id: productId, quantity, notes });
      toast.success('Restock request submitted');
      setProductId('');
      setQuantity(10);
      setNotes('');
      fetchData();
    } catch (e) {
      toast.error('Failed to submit');
    }
  };

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.restock')}</h1>

      <Card className="p-6 space-y-4 mb-6">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>New Restock Request</h2>
        <div>
          <label className="text-sm font-semibold">Product</label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="restock-product-select" className="mt-2"><SelectValue placeholder="Select product" /></SelectTrigger>
            <SelectContent>
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-semibold">Quantity</label>
          <Input data-testid="restock-qty-input" type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value))} className="mt-2" />
        </div>
        <div>
          <label className="text-sm font-semibold">Notes (optional)</label>
          <Textarea data-testid="restock-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2" placeholder="Additional details..." />
        </div>
        <Button data-testid="submit-restock-btn" onClick={handleSubmit} className="w-full" style={{ backgroundColor: '#1E3A8A' }}>Submit Request</Button>
      </Card>

      <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Manrope' }}>Previous Requests</h2>
      <div className="space-y-3">
        {requests.map((r) => {
          const prod = products.find(p => p.id === r.product_id);
          return (
            <Card key={r.id} className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">{prod?.name || 'Unknown'}</p>
                  <p className="text-sm text-slate-600">Qty: {r.quantity}</p>
                  {r.notes && <p className="text-sm text-slate-500 mt-1">{r.notes}</p>}
                  <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  r.status === 'fulfilled' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>{r.status.toUpperCase()}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
